import React from 'react';
import styled from 'styled-components';
import { system, extractSysProps } from './system';

// Replicates @actovos-consulting-group/ui-core Grid:
//   Grid.Row — CSS grid container (display:grid, grid-template-columns, grid-gap)
//     props: columns (number), gutter (number), auto (bool)
//   Grid.Column — grid-column: span N
//     props: size (number | { _?: number, md?: number, lg?: number, ... }), gutter (system prop treated as margin)
//
// The ui-core Grid is completely separate from molekule's Row/Column.
// app uses: Grid.Row auto, Grid.Column size={{ _: 12, md: 6, lg: 4 }}, gutter={20}

// Breakpoints used in ui-core Grid (from source inspection — uses theme.grid which comes from ui-core theme)
// The app's theme doesn't have theme.grid, so fall back to reasonable defaults.
const BREAKPOINTS: Record<string, string> = {
  _: '0px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1500px',
};

const DEFAULT_COLUMNS = 12;
const DEFAULT_GUTTER = 16;

// ---- Grid.Row ----
interface RowInternalProps {
  $columns?: number;
  $gutter?: number;
  $auto?: boolean;
  $sys: Record<string, any>;
  [key: string]: any;
}

const StyledGridRow = styled.div<RowInternalProps>`
  display: grid;
  grid-template-columns: repeat(
    ${(p: any) => (p.$auto ? 'auto-fit' : p.$columns || DEFAULT_COLUMNS)},
    minmax(1px, 1fr)
  );
  grid-gap: ${(p: any) => (p.$gutter !== undefined ? p.$gutter : DEFAULT_GUTTER)}px;
  ${(p: any) => system(p.$sys, p.theme)}
`;

type GridRowProps = {
  columns?: number;
  gutter?: number;
  auto?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
};

const GridRow: React.FC<GridRowProps> = ({
  columns,
  gutter,
  auto,
  children,
  ...props
}) => {
  const { sys, rest } = extractSysProps(props);
  return (
    <StyledGridRow
      $columns={columns}
      $gutter={gutter}
      $auto={auto}
      $sys={sys}
      {...rest}
    >
      {children}
    </StyledGridRow>
  );
};

// ---- Grid.Column ----
// size: number → grid-column: span N
// size: object like { _: 12, md: 6, lg: 4 } → responsive grid-column spans
// gutter in Grid.Column context is used as margin (from InfoCard call site: <Grid.Column gutter={20}>)

type ColumnSize = number | Partial<Record<keyof typeof BREAKPOINTS, number>>;

interface GridColumnInternalProps {
  $size?: ColumnSize;
  $sys: Record<string, any>;
  [key: string]: any;
}

function buildGridColumnCSS(size: ColumnSize | undefined): string {
  if (size === undefined) return '';
  if (typeof size === 'number') {
    return `grid-column: span ${size};`;
  }
  // Responsive object
  const parts: string[] = [];
  if (size._ !== undefined) {
    parts.push(`grid-column: span ${size._};`);
  }
  const keys: Array<keyof typeof BREAKPOINTS> = ['sm', 'md', 'lg', 'xl'];
  for (const key of keys) {
    if (size[key] !== undefined) {
      parts.push(
        `@media (min-width: ${BREAKPOINTS[key]}) { grid-column: span ${size[key]}; }`,
      );
    }
  }
  return parts.join('\n');
}

const StyledGridColumn = styled.div<GridColumnInternalProps>`
  ${(p: any) => buildGridColumnCSS(p.$size)}
  ${(p: any) => system(p.$sys, p.theme)}
`;

type GridColumnProps = {
  size?: ColumnSize;
  // gutter as margin shorthand (InfoCard usage)
  gutter?: number;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
};

const GridColumn: React.FC<GridColumnProps> = ({
  size,
  gutter,
  children,
  ...props
}) => {
  const { sys, rest } = extractSysProps(props);
  // gutter in Grid.Column is used as margin
  if (gutter !== undefined) {
    sys.m = `${gutter}px`;
  }
  return (
    <StyledGridColumn $size={size} $sys={sys} {...rest}>
      {children}
    </StyledGridColumn>
  );
};

// ---- Exported Grid object ----
export const Grid = {
  Row: GridRow,
  Column: GridColumn,
};

export default Grid;
